/**
 * Showing part of a long list, and saying honestly how much is left.
 *
 * A stocked kitchen is thirty or forty things, and a bucket that renders all of
 * them turns the home screen into a scroll. The screen's job is "what needs you
 * today" — the full inventory is a reference, and a reference belongs behind one
 * tap rather than in front of the thing you came for.
 *
 * Pure, because the arithmetic is where this goes wrong: an off-by-one that
 * offers "show 12 more" when three remain, or a button that stays on screen
 * after everything is visible, is the kind of thing you only catch by trying
 * every list length.
 */

/** How many rows a long bucket shows before it asks. */
export const FIRST_PAGE = 8;
/** How many more each tap reveals. */
export const PAGE = 12;

export interface Paging {
  /** How many rows to render right now. */
  visible: number;
  /** How many are hidden. Zero means the button should not be rendered. */
  hidden: number;
  /** What the button should say. Empty when there is nothing left to show. */
  label: string;
  /** The cap after one more tap. */
  next: number;
}

export function page(total: number, cap: number, step = PAGE): Paging {
  const visible = Math.max(0, Math.min(cap, total));
  const hidden = Math.max(0, total - visible);

  return {
    visible,
    hidden,
    /**
     * "Show the last 4" rather than "Show 12 more" when fewer than a page
     * remain. Promising twelve and delivering four is a small lie that makes
     * the count above the button look wrong.
     */
    label:
      hidden === 0
        ? ""
        : hidden <= step
          ? `Show the last ${hidden}`
          : `Show ${step} more`,
    next: visible + step,
  };
}
